import {
  AlertTriangle,
  Briefcase,
  Cake,
  Calendar,
  CheckCircle2,
  ClipboardCheck,
  CircleAlert,
  ChevronLeft,
  Eye,
  Mail,
  Phone,
  RadioTower,
  Save,
  Sparkles,
  Tag,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { toast } from 'sonner';
import type { Candidate, CandidateApplicationHistory, CandidateStage } from '../../types';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import { Textarea } from '../../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { createApplicationNote } from '../../libs/api/pipelineService';
import { getApiErrorMessage } from '../../libs/api/errorHandler';
import CvPreviewModal from './CvPreviewModal';
import { syncCandidateInterviewCaches, syncCandidateNoteCaches } from './candidateData';
import { listInterviews } from '../../libs/api/interviewService';
import {
  formatInterviewRound,
  getInterviewRoundBadgeClass,
  getInterviewRoundRank,
} from './candidateInterviewUtils';
import { formatVnPhoneForDisplay } from './candidateListUtils';
import { formatDisplayDate } from '@/libs/date/localDate';

interface CandidateProfileProps {
  candidate: Candidate;
  onBack: () => void;
}

const stageTone: Record<CandidateStage, string> = {
  New: 'text-muted-foreground',
  Screening: 'text-info',
  Interview: 'text-violet-700 dark:text-violet-300',
  Offer: 'text-warning-muted-foreground',
  Hired: 'text-success',
  Rejected: 'text-destructive',
};

export default function CandidateProfile({ candidate, onBack }: CandidateProfileProps) {
  const queryClient = useQueryClient();
  const score = candidate.score || 0;
  const ringCircumference = 276;
  const ringOffset = ringCircumference - (score / 100) * ringCircumference;
  const hasThreshold = typeof candidate.jdThreshold === 'number';
  const isAboveThreshold = hasThreshold ? score >= (candidate.jdThreshold || 0) : null;
  const scoreTextClass =
    isAboveThreshold === null
      ? 'text-primary'
      : isAboveThreshold
        ? 'text-success'
        : 'text-destructive';
  const scoreStrokeColor =
    isAboveThreshold === null
      ? 'var(--color-primary)'
      : isAboveThreshold
        ? 'var(--color-status-hired)'
        : 'var(--color-status-rejected)';
  const history: CandidateApplicationHistory[] = candidate.applicationHistory || [];
  const [noteDraft, setNoteDraft] = useState(candidate.kanbanNote || '');
  const [cvPreview, setCvPreview] = useState<{
    resumeId: string;
    fileName?: string | null;
  } | null>(null);
  const { data: interviewsResponse } = useQuery({
    queryKey: ['interviews', candidate.applicationId],
    queryFn: () => listInterviews(candidate.applicationId!),
    enabled: Boolean(candidate.applicationId),
  });
  const interviews = interviewsResponse?.items ?? [];
  const assessmentInterview = useMemo(
    () =>
      [...interviews].sort((left, right) => {
        const roundDiff =
          getInterviewRoundRank(right.interview_round) -
          getInterviewRoundRank(left.interview_round);
        if (roundDiff !== 0) return roundDiff;

        return new Date(right.start_at).getTime() - new Date(left.start_at).getTime();
      })[0] ?? null,
    [interviews],
  );
  useEffect(() => {
    if (!interviewsResponse || !candidate.applicationId) return;

    syncCandidateInterviewCaches(
      queryClient,
      {
        applicationId: candidate.applicationId,
        candidateId: candidate.id,
      },
      {
        durationMinutes: assessmentInterview?.duration_minutes ?? null,
        result: assessmentInterview?.interview_result ?? null,
        round: assessmentInterview?.interview_round ?? null,
        startAt: assessmentInterview?.start_at ?? null,
      },
    );
  }, [assessmentInterview, candidate.applicationId, candidate.id, interviewsResponse, queryClient]);

  useEffect(() => {
    setNoteDraft(candidate.kanbanNote || '');
  }, [candidate.id, candidate.kanbanNote]);

  const handleSaveNote = async () => {
    const noteText = noteDraft.trim();
    if (!noteText) {
      toast.error('Note must not be empty');
      return;
    }

    if (!candidate.applicationId) {
      toast.error('Application ID is missing');
      return;
    }

    try {
      const createdNote = await createApplicationNote(candidate.applicationId, {
        note_text: noteText,
      });

      syncCandidateNoteCaches(
        queryClient,
        {
          applicationId: candidate.applicationId,
          candidateId: candidate.id,
        },
        createdNote.note_text,
      );

      queryClient.invalidateQueries({ queryKey: ['pipeline-board'] });
      toast.success('Note saved successfully');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to save note'));
    }
  };

  const skillExperienceData = [...(candidate.skills || [])]
    .map((item) => ({
      skill: item.skill.trim(),
      years: typeof item.years === 'number' && Number.isFinite(item.years) ? item.years : 0,
    }))
    .filter((item) => Boolean(item.skill))
    .filter((item, index, arr) => arr.findIndex((entry) => entry.skill === item.skill) === index)
    .sort((a, b) => b.years - a.years)
    .slice(0, 10);

  const computedBarSize =
    skillExperienceData.length <= 2 ? 48 : skillExperienceData.length <= 4 ? 32 : 20;

  const openCvPreview = (resumeId: string, fileName?: string | null) => {
    setCvPreview({ resumeId, fileName });
  };

  return (
    <div className="space-y-6 pb-8">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 text-base">
          <Button
            variant="ghost"
            size="icon"
            onClick={onBack}
            className="rounded-full text-text-subtle hover:text-text-main"
          >
            <ChevronLeft size={18} />
          </Button>
          <span className="text-text-subtle">Candidates</span>
          <span className="text-text-subtle">/</span>
          <span className="font-semibold text-text-main">{candidate.name}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="text-sm"
            onClick={() => {
              openCvPreview(candidate.resumeId, candidate.resumeOriginalFileName);
            }}
          >
            <Eye size={14} /> View CV
          </Button>
        </div>
      </div>

      <Card className="gap-0 border-border-subtle p-6">
        <div className="grid gap-6 lg:grid-cols-[1.15fr_1fr_1fr_auto]">
          <section className="min-w-0 lg:border-r lg:border-border-subtle lg:pr-6">
            <div className="flex items-start gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg border border-primary/10 bg-primary-light text-xl font-bold text-primary shadow-sm">
                {candidate.name.charAt(0)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <h2 className="truncate text-3xl font-bold leading-9 text-text-main">
                    {candidate.name}
                  </h2>
                </div>
                <div className="mt-4 text-[10px] font-bold uppercase text-text-subtle">
                  Contact &amp; Role
                </div>
                <div className="mt-2 space-y-2 text-sm font-medium text-text-main">
                  <div className="flex min-w-0 items-center gap-2">
                    <Briefcase size={14} className="shrink-0 text-text-subtle" />
                    <span className="truncate">{candidate.role}</span>
                  </div>
                  <div className="flex min-w-0 items-center gap-2">
                    <Mail size={14} className="shrink-0 text-text-subtle" />
                    <span className="truncate">{candidate.email || '--'}</span>
                  </div>
                  <div className="flex min-w-0 items-center gap-2">
                    <Phone size={14} className="shrink-0 text-text-subtle" />
                    <span className="truncate">
                      {candidate.phone ? formatVnPhoneForDisplay(candidate.phone) : '--'}
                    </span>
                  </div>
                  <div className="flex min-w-0 items-center gap-2">
                    <Cake size={14} className="shrink-0 text-text-subtle" />
                    <span>DOB: {formatDisplayDate(candidate.dateOfBirth ?? '')}</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="min-w-0 lg:border-r lg:border-border-subtle lg:pr-6">
            <div className="mb-3 text-[10px] font-bold uppercase text-text-subtle">
              Source &amp; Channel
            </div>
            <div className="space-y-2 text-sm text-text-main">
              <div className="flex min-w-0 items-center gap-2">
                <Tag size={14} className="shrink-0 text-text-subtle" />
                <span className="font-medium text-text-subtle">Source:</span>
                <Badge
                  variant="outline"
                  className="max-w-44 truncate border-border-subtle bg-bg-main px-2 py-0 text-xs font-semibold text-text-main"
                >
                  {candidate.source || '--'}
                </Badge>
              </div>
              <div className="flex min-w-0 items-center gap-2">
                <RadioTower size={14} className="shrink-0 text-text-subtle" />
                <span className="font-medium text-text-subtle">Channel:</span>
                <Badge
                  variant="outline"
                  className="max-w-44 truncate border-primary/20 bg-primary-light px-2 py-0 text-xs font-semibold text-primary"
                >
                  {candidate.channel || '--'}
                </Badge>
              </div>
              <div className="flex min-w-0 items-center gap-2">
                <Calendar size={14} className="shrink-0 text-text-subtle" />
                <span className="font-medium text-text-subtle">Applied:</span>
                <span className="font-semibold">{formatDisplayDate(candidate.appliedDate)}</span>
              </div>
            </div>
          </section>

          <section className="min-w-0">
            <div className="mb-3 text-[10px] font-bold uppercase text-text-subtle">
              Interview Assessment
            </div>
            <div className="space-y-2 text-sm text-text-main">
              <div className="flex min-w-0 items-center gap-2">
                <Calendar size={14} className="shrink-0 text-text-subtle" />
                <span className="font-medium text-text-subtle">Interview Day:</span>
                <span className="font-semibold">
                  {assessmentInterview ? formatDisplayDate(assessmentInterview.start_at) : 'N/A'}
                </span>
              </div>
              <div className="flex min-w-0 items-center gap-2">
                <Tag size={14} className="shrink-0 text-text-subtle" />
                <span className="font-medium text-text-subtle">Interview Round:</span>
                <Badge
                  className={`h-6 min-w-12 justify-center border px-2 text-xs font-bold ${getInterviewRoundBadgeClass(
                    assessmentInterview?.interview_result,
                  )}`}
                >
                  {assessmentInterview
                    ? formatInterviewRound(assessmentInterview.interview_round)
                    : 'N/A'}
                </Badge>
              </div>
              <div className="flex min-w-0 items-center gap-2">
                <ClipboardCheck size={14} className="shrink-0 text-text-subtle" />
                <span className="font-medium text-text-subtle">Interview Result:</span>
                <Badge
                  variant="outline"
                  className={
                    assessmentInterview?.interview_result === 'PASSED'
                      ? 'border-success/30 bg-success-muted px-2 py-0 text-xs font-semibold text-success-muted-foreground'
                      : assessmentInterview?.interview_result === 'FAILED'
                        ? 'border-error/30 bg-error-muted px-2 py-0 text-xs font-semibold text-error-muted-foreground'
                        : 'border-border-subtle bg-bg-main px-2 py-0 text-xs font-semibold text-text-subtle'
                  }
                >
                  {assessmentInterview?.interview_result ?? 'N/A'}
                </Badge>
              </div>
            </div>
          </section>

          <section className="flex min-w-52 flex-col items-center justify-center border-t border-border-subtle pt-5 lg:border-l lg:border-t-0 lg:pl-6 lg:pt-0">
            <div className="text-center text-base font-bold text-text-main">AI Match Score</div>
            <div className="relative mt-3 size-40 shrink-0">
              <svg width="160" height="160" viewBox="0 0 120 120" className="-rotate-90">
                <circle
                  cx="60"
                  cy="60"
                  r="44"
                  stroke="var(--color-border-subtle)"
                  strokeWidth="10"
                  fill="none"
                />
                <circle
                  cx="60"
                  cy="60"
                  r="44"
                  stroke={scoreStrokeColor}
                  strokeWidth="10"
                  fill="none"
                  strokeLinecap="round"
                  strokeDasharray={ringCircumference}
                  strokeDashoffset={ringOffset}
                />
              </svg>
              <div className="absolute inset-0 grid place-items-center text-center">
                <div>
                  <div className={`text-3xl font-bold ${scoreTextClass}`}>{score}%</div>
                  <div className="mt-1 text-xs font-medium uppercase text-text-subtle">
                    Precision
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </Card>

      <section className="grid grid-cols-1 xl:grid-cols-4 gap-5">
        <Card className="xl:col-span-3 p-7 min-h-92 gap-0 border-border-subtle">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-bold text-text-main">Skill Dimension Analysis</h3>
            <Badge className="text-xs font-bold uppercase text-primary bg-primary-light">
              Column Chart
            </Badge>
          </div>
          <div className="h-60">
            {skillExperienceData.length === 0 ? (
              <div className="h-full grid place-items-center text-sm text-text-subtle">
                No skill data available.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={skillExperienceData}
                  barCategoryGap={skillExperienceData.length <= 2 ? '35%' : '20%'}
                  margin={{
                    top: 8,
                    right: 24,
                    left: 8,
                    bottom: 40,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis
                    dataKey="skill"
                    interval={0}
                    angle={-20}
                    textAnchor="end"
                    height={55}
                    tick={{ fill: '#5E6C84', fontSize: 11 }}
                  />
                  <YAxis
                    allowDecimals
                    tick={{ fill: '#5E6C84', fontSize: 11 }}
                    label={{
                      value: 'Years',
                      angle: -90,
                      position: 'insideLeft',
                      fill: '#5E6C84',
                    }}
                  />
                  <Tooltip
                    formatter={(value) => {
                      const years = typeof value === 'number' ? value : Number(value ?? 0);
                      return [`${years} years`, 'Experience'];
                    }}
                    labelFormatter={(label) => `Skill: ${label}`}
                  />
                  <Bar
                    dataKey="years"
                    fill="#0F4CAB"
                    radius={[6, 6, 0, 0]}
                    barSize={computedBarSize}
                    maxBarSize={48}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        <Card className="xl:col-span-1 p-7 min-h-92 gap-0 border-border-subtle">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-bold text-text-main">Note</h3>
            <Badge variant="outline" className="text-[11px] uppercase">
              Candidate Note
            </Badge>
          </div>
          <p className="text-sm text-text-subtle mb-3">Recruiter can edit note directly</p>
          <Textarea
            rows={10}
            value={noteDraft}
            onChange={(e) => setNoteDraft(e.target.value)}
            placeholder="Write note..."
            className="resize-none h-50"
          />
          <div className="mt-3 flex justify-end">
            <Button type="button" size="sm" onClick={() => void handleSaveNote()}>
              <Save size={14} /> Save
            </Button>
          </div>
        </Card>
      </section>

      <section className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        <Card className="p-6 min-h-64 gap-0 border-border-subtle">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 size={16} className="text-success" />
            <h3 className="text-base font-bold text-text-main">Key Strengths</h3>
          </div>
          <ul className="space-y-2">
            {(candidate.pros || []).map((pro, index) => (
              <li key={index} className="text-sm text-text-main flex items-start gap-2 leading-5">
                <span className="mt-1 w-1.5 h-1.5 rounded-full bg-success shrink-0" />
                <span>{pro}</span>
              </li>
            ))}
          </ul>
        </Card>

        <Card className="p-6 min-h-64 gap-0 border-border-subtle">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={16} className="text-destructive" />
            <h3 className="text-base font-bold text-text-main">Potential Gaps</h3>
          </div>
          <ul className="space-y-2">
            {(candidate.cons || []).map((con, index) => (
              <li key={index} className="text-sm text-text-main flex items-start gap-2 leading-5">
                <span className="mt-1 w-1.5 h-1.5 rounded-full bg-destructive shrink-0" />
                <span>{con}</span>
              </li>
            ))}
          </ul>
        </Card>

        <Card className="rounded-xl p-6 min-h-64 gap-0 bg-linear-to-br from-primary to-secondary text-primary-foreground border-0">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={16} className="text-primary-foreground/70" />
            <h3 className="text-base font-bold">AI Executive Summary</h3>
          </div>
          <p className="text-sm leading-5 text-primary-foreground/80">{candidate.summary || ''}</p>
        </Card>
      </section>

      <Card className="p-0 gap-0 border-border-subtle">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-text-main">Application History</h3>
            <span className="text-xs uppercase tracking-[0.18em] text-text-subtle font-bold">
              Total: {history.length} past roles
            </span>
          </div>

          <Table className="min-w-[860px] table-fixed">
            <TableHeader>
              <TableRow className="border-border-subtle/60 text-xs uppercase tracking-widest text-text-subtle">
                <TableHead className="w-[44%] py-2 font-bold">JD Name</TableHead>
                <TableHead className="w-[14%] py-2 font-bold">Apply Time</TableHead>
                <TableHead className="w-[16%] py-2 font-bold">Matching Score</TableHead>
                <TableHead className="w-[14%] py-2 font-bold">Final Stage</TableHead>
                <TableHead className="w-[6%] py-2 font-bold">Note</TableHead>
                <TableHead className="w-[6%] py-2 font-bold">View CV</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {history.map((item) => (
                <TableRow
                  key={item.id}
                  className="border-b last:border-0 border-border-subtle/70 text-base"
                >
                  <TableCell className="max-w-0 py-3 pr-6 text-left font-medium text-text-main">
                    <a
                      href={`/jobs/${item.jdId}/edit`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block truncate text-primary hover:underline"
                      title={item.jdName}
                      onClick={(event) => event.stopPropagation()}
                    >
                      {item.jdName}
                    </a>
                  </TableCell>
                  <TableCell className="py-3 text-center text-text-subtle">
                    <a
                      href={`/candidates/${item.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {formatDisplayDate(item.applyTime)}
                    </a>
                  </TableCell>
                  <TableCell className="py-3 text-center">
                    <Badge className="bg-muted text-accent-foreground text-sm font-bold">
                      {item.matchingScore}%
                    </Badge>
                  </TableCell>
                  <TableCell className="py-3 text-center">
                    <Badge
                      variant="secondary"
                      className={`text-sm font-semibold ${stageTone[item.finalStage]}`}
                    >
                      {item.finalStage}
                    </Badge>
                  </TableCell>
                  <TableCell className="py-3 text-center">
                    <div className="relative inline-flex items-center group">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        className="text-text-main hover:text-text-main"
                        aria-label={item.note || 'No note'}
                      >
                        <CircleAlert size={14} />
                      </Button>
                      <div className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-1 max-w-80 -translate-x-1/2 truncate whitespace-nowrap rounded bg-popover px-2 py-1 text-xs font-medium text-popover-foreground opacity-0 shadow-sm transition-opacity group-hover:opacity-100">
                        {item.note || 'No note'}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="py-3 text-center">
                    <div className="flex justify-center">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        className="text-text-subtle hover:text-text-main"
                        aria-label="View CV"
                        title="View CV"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          openCvPreview(item.resumeId, item.resumeOriginalFileName ?? null);
                        }}
                      >
                        <Eye size={14} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <CvPreviewModal
        open={cvPreview !== null}
        onOpenChange={(next) => {
          if (!next) {
            setCvPreview(null);
          }
        }}
        resumeId={cvPreview?.resumeId ?? null}
        fileName={cvPreview?.fileName}
      />
    </div>
  );
}
